/**
 * Force the web app to reload onto the latest deployed build.
 *
 * Clears the Cache Storage API entries and unregisters any leftover
 * service worker (old PWA shells caused "stuck on old version" reports),
 * then reloads with a cache-busting query param so Safari can't serve a
 * stale index.html that still points at the previous hashed bundle.
 *
 * The cache/SW cleanup is raced against a 5-second timeout: if a browser
 * extension or misbehaving service worker stalls the cleanup, we still
 * reload after the timeout instead of leaving the user stuck on a
 * "Refreshing…" button forever. Worst case the new bundle inherits some
 * stale cache, which the cache-bust query param handles for index.html.
 *
 * Web-only by nature: on the native (Capacitor) app the OTA bundle is
 * applied by Capgo on relaunch, not by a web reload — callers should gate
 * this to the web with `!Capacitor.isNativePlatform()`.
 *
 * Shared by VersionUpdateBanner (the toast) and AppVersionFooter (the
 * "Update available" status pill) so the reload behaviour lives in one place.
 */
export async function forceReloadToLatest(): Promise<void> {
  const cleanup = (async () => {
    try {
      if ("caches" in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch {
      /* cache API unavailable — proceed to reload anyway */
    }
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
    } catch {
      /* SW API unavailable — proceed */
    }
  })();
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 5000));
  await Promise.race([cleanup, timeout]);
  const url = new URL(window.location.href);
  url.searchParams.set("_v", Date.now().toString());
  window.location.replace(url.toString());
}
